import { useEffect, useState } from 'react'
import {
  Alert,
  Button,
  Checkbox,
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@ideon-api/ideon-ui'
import { parsedSdkErrorBody, sdkErrorMessage, useAuthServiceClient } from '../../lib/sdk-client'

// Widened from the SDK's `AuthDuplicateCandidateAccountRef` (PD-30009) --
// AccountSearchPage's checkbox-picked pair are `AccountListItem`s, which carry
// no `sfdcId` at all (the account-search response never returns it), so this
// dialog cannot require one. Do NOT narrow this back to
// `DuplicateAccountSummary`: its values (id/createdAt/required sfdcId) still
// satisfy this shape structurally, so the duplicate-accounts-drawer caller
// keeps working, but AccountSearchPage would stop type-checking.
export interface MergeCandidateAccount {
  slug: string
  name: string
  sfdcId?: string | null
}

interface ConfirmMergeDialogProps {
  isOpen: boolean
  onClose: () => void
  survivor: MergeCandidateAccount
  loser: MergeCandidateAccount
  onMerged: () => void
}

export function ConfirmMergeDialog({ isOpen, onClose, survivor, loser, onMerged }: ConfirmMergeDialogProps) {
  const getClient = useAuthServiceClient()
  const [overrideConflict, setOverrideConflict] = useState(false)
  const [submitting, setSubmitting] = useState(false)
  const [error, setError] = useState<string | null>(null)
  // Set reactively from a 409 `account_merge_conflict` response -- the
  // client-side `hasSfdcConflict` guess only knows what the caller passed in,
  // which for an AccountSearchPage-originated pair is never (see
  // MergeCandidateAccount above). Distinct from `account_merge_resource_conflict`,
  // which has no override path and must never show this checkbox.
  const [serverSfdcConflict, setServerSfdcConflict] = useState(false)

  // This component instance persists across different candidate selections
  // (the drawer renders one ConfirmMergeDialog for whichever pair is
  // current), so a checked override from a prior merge must not carry over
  // to the next pair's own conflict -- each pair needs its own confirmation.
  useEffect(() => {
    setOverrideConflict(false)
    setServerSfdcConflict(false)
    setError(null)
  }, [survivor.slug, loser.slug])

  const hasSfdcConflict = Boolean(survivor.sfdcId && loser.sfdcId && survivor.sfdcId !== loser.sfdcId)
  const showOverrideCheckbox = hasSfdcConflict || serverSfdcConflict
  const canMerge = !showOverrideCheckbox || overrideConflict

  const handleOpenChange = (open: boolean) => {
    if (open || submitting) return
    setOverrideConflict(false)
    setError(null)
    onClose()
  }

  const handleMerge = async () => {
    setSubmitting(true)
    setError(null)
    try {
      const client = await getClient()
      await client.auth.mergeAccounts(survivor.slug, {
        sourceSlug: loser.slug,
        overrideSfdcConflict: overrideConflict,
      })
      onMerged()
    } catch (mergeError) {
      // The SDK's per-status error classes (AuthAccountMergeConflictResponse,
      // AuthAccountMergeResourceConflictResponse) aren't exported at runtime
      // (see use-bind-identifier.ts) -- branch on the thrown object's
      // `metadata.status` and read `.message`/`.error` structurally instead.
      // `parsedSdkErrorBody` reparses the raw response when the SDK's own
      // content-type sniffing missed it (see its comment in sdk-client.ts) --
      // required here since this API's 409s always carry that charset.
      const { status, body } = parsedSdkErrorBody(mergeError)
      const errorCode = typeof body.error === 'string' ? body.error : undefined

      // Only `account_merge_conflict` (the recoverable SFDC conflict) reveals
      // the override checkbox -- `account_merge_resource_conflict` (entitlements/
      // service_accounts) has no override path, ever (docs/knowledge/flows/
      // account-merge.md).
      if (status === 409) {
        setServerSfdcConflict(errorCode === 'account_merge_conflict')
      }

      // A client-side abort/timeout can't roll back a merge that already
      // committed server-side, so treat it as "unknown, don't assume it
      // failed" rather than a plain error. `AbortSignal.timeout()` (used
      // once `timeoutMs` is set on the SDK client) raises a `TimeoutError`,
      // not `AbortError` -- match both.
      const message =
        status === 409
          ? sdkErrorMessage(mergeError, 'Failed to merge accounts.')
          : status === 403
            ? 'Merging accounts requires an admin role.'
            : mergeError instanceof DOMException &&
                (mergeError.name === 'TimeoutError' || mergeError.name === 'AbortError')
              ? 'This may have completed -- refresh before retrying.'
              : sdkErrorMessage(mergeError, 'Failed to merge accounts.')
      setError(message)
    } finally {
      setSubmitting(false)
    }
  }

  return (
    <Dialog open={isOpen} onOpenChange={handleOpenChange}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Confirm merge</DialogTitle>
        </DialogHeader>

        <p className="text-sm text-ideon-gray-70">
          <span className="font-semibold text-ideon-gray-black">
            {survivor.name} ({survivor.slug})
          </span>{' '}
          will absorb all identifiers, domains, users, entitlements, and service accounts from{' '}
          <span className="font-semibold text-ideon-gray-black">
            {loser.name} ({loser.slug})
          </span>
          , which will then be archived. If both accounts already hold entitlements or service accounts, the merge
          will be rejected rather than silently picking a winner. This cannot be undone.
        </p>

        {showOverrideCheckbox && (
          <Alert variant="error">
            <div className="flex flex-col gap-2">
              {hasSfdcConflict ? (
                <p>
                  Both accounts have a different SFDC ID. {survivor.name}&rsquo;s ID (
                  <span className="font-semibold">{survivor.sfdcId}</span>) will be kept;{' '}
                  {loser.name}&rsquo;s ID (<span className="font-semibold">{loser.sfdcId}</span>) will be discarded.
                </p>
              ) : (
                <p>
                  Both accounts have a different SFDC ID. {survivor.name}&rsquo;s ID will be kept;{' '}
                  {loser.name}&rsquo;s ID will be discarded.
                </p>
              )}
              <label className="flex items-center gap-2">
                <Checkbox
                  checked={overrideConflict}
                  onCheckedChange={(checked) => setOverrideConflict(checked === true)}
                />
                <span>I understand -- override the conflict and keep {survivor.name}&rsquo;s SFDC ID</span>
              </label>
            </div>
          </Alert>
        )}

        {error && <Alert variant="error">{error}</Alert>}

        <DialogFooter>
          <Button variant="text" onClick={() => handleOpenChange(false)} disabled={submitting}>
            Cancel
          </Button>
          <Button variant="danger" onClick={handleMerge} disabled={!canMerge || submitting}>
            Merge anyway
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}
